import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { PlusIcon } from 'lucide-react';
import { useExperience } from '../../contexts/ExperienceContext';
import { weekCases } from '../../data/journeys';
import { needById } from '../../data/needs';
import { brand } from '../../data/palette';
import { productById } from '../../data/products';
import { Advance } from '../ui/Advance';
import { Eyebrow, Stage, StageTitle } from '../ui/Stage';
const ease = [0.16, 1, 0.3, 1] as const;

/** Recognisable situations from a normal week, each mapped to the family. */
export function WeekStage() {
  const {
    signals,
    push,
    next,
    direction
  } = useExperience();
  const [open, setOpen] = useState<string | undefined>();
  const toggle = (id: string) => {
    setOpen(open === id ? undefined : id);
    push('exploredCases', id);
  };
  return <Stage direction={direction} align="start" className="min-h-screen py-28 sm:py-32" labelledBy="week-title" data-id="element-377" data-mp="19a0sfbdwpb4b">
      <div className="max-w-2xl" data-id="element-378" data-mp="297ozv5h9p8y8">
        <Eyebrow accent={brand.sky} data-id="element-379" data-mp="296e0dhuuuncx">Your typical week</Eyebrow>
        <StageTitle id="week-title" size="display-sm" className="mt-7" data-id="element-380" data-mp="2d87ut8egiv4n">
          Want to see what Filtek could cover in your typical week?
        </StageTitle>
      </div>

      <ul className="mt-14 border-t border-enamel/10" data-id="element-381" data-mp="2i612k5verx71">
        {weekCases.map(item => {
        const need = needById(item.need);
        const product = productById(need.productId);
        const isOpen = open === item.id;
        const isSeen = signals.exploredCases.includes(item.id);
        return <li key={item.id} className="border-b border-enamel/10" data-id="element-382" data-mp="1zhm9ld1j693l">
              <button type="button" onClick={() => toggle(item.id)} aria-expanded={isOpen} className="group flex w-full items-center gap-5 py-5 text-left sm:gap-8" data-id="element-383" data-mp="1zf9judpj6ufz">
                <span className="label-mono w-28 shrink-0 text-[9px] text-mute/70 sm:w-36" data-id="element-384" data-mp="1br033q76cwvp">
                  {item.frequency}
                </span>
                <span className="flex-1 text-lg font-light leading-snug tracking-[-0.02em] transition-colors duration-500 sm:text-2xl" style={{
              color: isOpen ? need.accent : isSeen ? 'var(--enamel)' : brand.quiet
            }} data-id="element-385" data-mp="13ozedell6t7n">
                  {item.situation}
                </span>
                <motion.span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border" animate={{
              rotate: isOpen ? 135 : 0,
              borderColor: isOpen ? need.accent : 'rgba(244,242,237,0.2)'
            }} transition={{
              duration: 0.5,
              ease
            }} data-id="element-386" data-mp="kry8lrk7kp2n">
                  <PlusIcon className="h-3.5 w-3.5 text-enamel" aria-hidden="true" data-id="element-387" data-mp="ptnmrg2q9iuy" />
                </motion.span>
              </button>

              <AnimatePresence initial={false} data-id="element-388" data-mp="217996th7d6cs">
                {isOpen && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.6,
              ease
            }} className="overflow-hidden" data-id="element-389" data-mp="3j4cntrhabo2d">
                    <div className="grid gap-8 pb-10 sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)] sm:gap-10 sm:pl-[9rem]" data-id="element-390" data-mp="3f1pu1l11zt2j">
                      {item.image && <img src={item.image} alt={item.situation} className="h-36 w-full object-cover opacity-85 sm:h-40" data-id="element-391" data-mp="ao9p4b85h9q6" />}
                      <div data-id="element-392" data-mp="1uo9yqs2pqg8">
                        <p className="label-mono text-[9px]" style={{
                    color: need.accent
                  }} data-id="element-393" data-mp="1tv0vj5n6oux">
                          {need.label}
                        </p>
                        <p className="mt-3 text-base font-light leading-relaxed text-enamel" data-id="element-394" data-mp="udbpzrs7qfag">
                          {item.coverage}
                        </p>
                        <p className="mt-4 text-sm font-light text-mute" data-id="element-395" data-mp="2wx0gkk1lt05x">
                          Filtek <span className="text-enamel" data-id="element-396" data-mp="2wz3dzp535vko">{product.shortName}</span> ·{' '}
                          {need.technique}
                        </p>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </li>;
      })}
      </ul>

      {signals.exploredCases.length > 0 && <motion.div initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6,
      ease
    }} className="mt-14 flex flex-wrap items-center justify-between gap-6" data-id="element-397" data-mp="3glj7b629clmq">
          <p className="label-mono text-[10px] text-mute" data-id="element-398" data-mp="84o943wenpez">
            {signals.exploredCases.length} situation
            {signals.exploredCases.length > 1 ? 's' : ''} explored
          </p>
          <Advance label="Continue" onClick={next} accent={brand.sky} data-id="element-399" data-mp="apm4da7l2rg0" />
        </motion.div>}
    </Stage>;
}